import { Info } from "lucide-react";
import React, { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAddProactiveChat } from "@/api/pro-active-chat/create-proactive-chat";
import useGlobalPermission from "@/store/user-permissions";

interface EasySetupSettingsProps {
    className?: string;
}

const EasySetupSettings: React.FC<EasySetupSettingsProps> = ({ className }) => {
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState({ title: "", message: "" });

    const { mutate: addProactiveChat } = useAddProactiveChat();

    const handleSubmit = () => {
        if (!title || !message) {
            setErrors({ title: title ? "" : "Title is required", message: message ? "" : "Message is required" });
            return;
        }
        addProactiveChat({
            title,
            message,
            url: "",
            is_active: true,
            delay: 2,
            scrawl_percentage: 0,
        });
        setTitle("");
        setMessage("");
        setErrors({ title: "", message: "" });
    };

    const { permissionList } = useGlobalPermission();
    const proactivePermission = permissionList.find((item) => item.module.name === "Proactive_Chat");
    return (
        <div className={cn(className)}>
            <div>
                <h3 className="text-xl font-bold text-gray-700">Title of Trigger(s)</h3>

                <Input
                    placeholder={"Enter Trigger Name Here"}
                    className="mt-2 h-12 p-4 text-xl"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={!proactivePermission?.can_create}
                />
                <small className="text-xs text-red-700">{errors?.title}</small>
                <h3 className="mt-4 text-xl font-bold text-gray-700">Write Your Message</h3>
                <p className="mb-4 text-sm text-gray-700">
                    This message will appear above the chat bubble as a &quot;preview&quot;. If the visitor close the preview message it will not show
                    again.
                </p>
                <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Hello, I'm Shoe AI Chatbot and I'm here to help you with your queries. How can I help you today?"
                    className="mt-2 h-20 p-4 text-xl"
                    disabled={!proactivePermission?.can_create}
                />
                <small className="text-xs text-red-700">{errors?.message}</small>
                <div className="mt-2 flex items-center gap-2">
                    <Info className="size-4 text-gray-600" />
                    <p className="text-sm text-gray-700">This trigger will appear on all pages after 2 seconds on page load</p>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-4">
                    <Button disabled={!proactivePermission?.can_create} variant={"outline"} className="py-5 text-xl text-gray-600">
                        Cancel
                    </Button>
                    <Button disabled={!proactivePermission?.can_create} className="py-5 text-xl" onClick={handleSubmit}>
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EasySetupSettings;
